import { CallToAction, EOutcomeType } from './outcome';

export interface Dictionary {
  [k: string]: any;
}

export enum EQuestionType {
  Boolean = 'Boolean',
  Select = 'Select',
  MultiSelect = 'MultiSelect',
  Picture = 'Picture',
  Multiple = 'Multiple',
  LeadGeneration = 'LeadGeneration',
  Email = 'Email',
  Name = 'Name',
  PostalCode = 'PostalCode',
  TextField = 'TextField',
  Number = 'Number',
  Date = 'Date'
}

export interface QuestionNode {
  id: string;
  heading: string;
  title: string;
  helperText: string;
  buttonSubmitText: string;
  type: EQuestionType;
  questionnaireId: string;
  created: string;
  modified: string;
  answers: Answer[];
  fields: Field[];
  index: number;
  multipleSelection: boolean;
}

export interface Field {
  id?: string;
  title: string;
  fieldKey: string;
  helperText: string;
  required: boolean;
  validateEmail: boolean;
  checkBoxText: string;
  checked: boolean;
  type: EFieldType;
  sortOrder: number;
  questionId?: string;
  dropdownOptions: DropdownOption[];
  placeholderText: string;
}

export interface Answer {
  id: string;
  text: string;
  helperText: string;
  imageUrl: string;
  sortOrder: number;
  priceImpact: string;
  questionId: string;
  nextQuestionId: string;
  created: string;
  modified: string;
  isVirtual?: boolean;
  required?: boolean;
  validate?: boolean;
  type?: string;
}

export enum EFieldType {
  Email = 'Email',
  PhoneNumber = 'PhoneNumber',
  Address = 'Address',
  TextBox = 'TextBox',
  Checkbox = 'Checkbox',
  Dropdown = 'Dropdown',
  DatePicker = 'DatePicker',
  TimePicker = 'TimePicker',
  CompanyName = 'CompanyName',
  CompanyURL = 'CompanyURL',
  FirstName = 'FirstName',
  LastName = 'LastName',
  Textarea = 'Textarea',
  Discount = 'Discount'
}

export interface DropdownOption {
  id?: string;
  title: string;
  fieldId?: string;
}

export interface QuestionnaireDetails {
  id: string;
  name: string;
  code: string;
  slug: string;
  status: string;
  description: string;
  startQuestionId: string;
  defaultOutcomeId: string;
  created: string;
  modified: string;
  questions: QuestionNode[];
  themeId: string;
  gtmContainerId: string;
  facebookPixelId: string;
  luckyOrangeSiteId: string;
  hideHeader: boolean;
  hideProgressBar: boolean;
  publishedAt: string;
  theme: QuestionnaireTheme | null;
}

export interface QuestionnaireProp {
  activeStep: number;
}

export interface CompleteQuestionnaireReq {
  id: string;
  questionnaireId: string;
  answerSelectionIds: string[];
  questionFieldSubmissions: QuestionFieldSubmission[];
  fieldAnswer?: Dictionary;
}

export interface CompleteQuestionnaireParam {
  id: string;
  questionnaireId: string;
  QuestionnairePublishedAt: string | null;
  Submission: { answerSelectionIds: string[]; questionFieldSubmissions: QuestionFieldSubmission[], paramFields: OptionalField[], answerDetails: AnswerDetail[]};
  PTID: string | null;
}

export interface AnswerDetail {
  question: string,
  answers: string[]
}

export interface QuestionnaireState extends Omit<CompleteQuestionnaireReq, 'questionFieldSubmissions'> {
  questionFieldSubmissions: Dictionary;
}

export interface QuestionFieldSubmission {
  fieldId: string;
  completedQuestionnaireId: string;
  content: string;
  location?: any;
}
export interface QuestionFieldStateSubmission {
  type: string;
  content: string;
}

export interface Answer {
  id: string;
  text: string;
  helperText: string;
  imageUrl: string;
  sortOrder: number;
  priceImpact: string;
  questionId: string;
  nextQuestionId: string;
  created: string;
  modified: string;
}

export interface QuestionnaireElements {
  questionText: string;
  helperText: string;
  answerText: string;
  answerHelperText: string;
  productTitle: string;
  contactTitle: string;
  contactFormHeadings: string;
  contactFormHintText: string;
  productHighlightsText: string;
  productDescription: string;
  headerBar: string;
  logo: string;
  logoBackgroundColor: string;
  progressBar: string;
  background: string;
  primaryButtonBackground: string;
  primaryButtonText: string;
  secondaryButtonBackground: string;
  secondaryButtonText: string;
  linkText: string;
  priceText: string;
  recommendedBackground: string;
  recommendedText: string;
  bulletPoints: string;
  answerCardSelectedState: string;
  iconButtons: string;
  helperIconLink: string;
  helperIcon: string;
  isTouchDevice?: boolean;
  websiteUrl?: string;
  bodyColor: string;
  appImages: Dictionary;
  googleKey?: string;
  isEmbed?: boolean;
  hideHeader?: boolean;
  currency?: {
    name: string;
    symbol: string;
  };
  defaultPicture: string;
  iconColor: string;
  fontTypeface: string;
}

export interface QuestionnaireTheme {
  id: string;
  title: string;
  isBaseTheme: boolean;
  fontTypeface: string;
  questionColor: string;
  bodyColor: string;
  productColor: string;
  topBarColor: string;
  progressBarColor: string;
  backgroundColor: string;
  buttonColor: string;
  buttonTextColor: string;
  recommendBackgroundColor: string;
  recommendTextColor: string;
  iconButtonsColor: string;
  iconImageColor: string;
  logoUrl: string;
  logoBackgroundColor: string;
  websiteUrl?: string;
  iconColor: string;
}

export interface Field {
  id?: string;
  title: string;
  helperText: string;
  required: boolean;
  validateEmail: boolean;
  checkBoxText: string;
  checked: boolean;
  type: EFieldType;
  sortOrder: number;
  questionId?: string;
  dropdownOptions: DropdownOption[];
  placeholderText: string;
}

export interface Lead {
  firstName?: string;
  lastName?: string;
  email?: string;
  contact?: string;
  location?: string;
  completedQuestionnaireId: string;
  recommendedProducts: any;
  optionalFields: OptionalField[];
  questionnaireInfo: any;
  outcomeThankYouInfo: ThankProps | null;
  hasOutcome?: boolean;
  latitude: null | number;
  longitude: null | number;
  outcomeType: EOutcomeType | null;
}

export interface OptionalField {
  fieldName: string;
  fieldKey: string;
  content: string;
  additionalText?: string;
}

export enum ECallToActionType {
  Email = 'Email',
  Phone = 'Phone',
  URL = 'URL',
  ViewDetails = 'ViewDetails',
  Video = 'Video',
  Payment = 'Payment',
  Schedule = 'Schedule',
  Redirect = 'Redirect'
}

export interface ThankProps {
  headerLabel?: string;
  message?: string;
  callToAction?: CallToAction[];
}

export interface LocationProps {
  id: string;
  name: string;
  polygons: string;
}

export interface PolygonType {
  lat: number;
  lng: number;
}

export enum EAnswerTypes {
  FirstName = 'First Name',
  LastName = 'Last Name',
  Email = 'Email',
  TextField = 'Text',
  Number = 'Phone',
  PostalCode = 'Postal code',
  Boolean = 'Radio Button',
  Select = 'Drop Down'
}

export interface LeadOutcome {
  completedQuestionnaireId: string;
  optionalFields: OptionalField[];
}
